/**
 * Read a listener activity list (`/user/requests`, `/user/tips`) as an array,
 * taking a 404 or 403 as "nothing here": the backend answers that way for an
 * account with no history of that kind, and the pages that show both lists
 * side by side (ListenerHistoryPage, NotificationsPage) still render the
 * other one. Any other failure is the caller's to report.
 *
 * The endpoints answer a page envelope (`{ requests: [...], total, ... }`,
 * `{ tips: [...], ... }`); pass the list's `key` to read it. Without one,
 * only a bare array counts.
 */
export async function fetchWithFallback(fetchWrapper, url, key) {
  try {
    const data = await fetchWrapper.get(url);
    if (Array.isArray(data)) return data;
    if (key && data && Array.isArray(data[key])) return data[key];
    return [];
  } catch (err) {
    if (err && (err.status === 404 || err.status === 403)) return [];
    throw err;
  }
}

/**
 * A request row's status label: `/user/requests` serves `status_label`
 * (`status` is the numeric code); older rows carried a string `status`.
 */
export function requestStatusLabel(request) {
  return request.status_label ?? request.statusLabel ?? (typeof request.status === 'string' ? request.status : null);
}

/** Whether a request row was played (`status_label: "completed"`, or the older "played"). */
export function isPlayedRequest(request) {
  const label = String(requestStatusLabel(request) ?? '').toLowerCase();
  return label === 'completed' || label === 'played';
}

/** A tip row's amount in dollars, from `amount_cents` or the older `amount`; null when neither is there. */
export function tipAmount(tip) {
  if (tip.amount != null) return Number(tip.amount);
  return tip.amount_cents != null ? Number(tip.amount_cents) / 100 : null;
}

/** Whether a tip went through: `/user/tips` also lists pending, failed and refunded ones. */
export function isConfirmedTip(tip) {
  return tip.status == null || String(tip.status).toLowerCase() === 'confirmed';
}
